import {Button, Stack, TextField, Checkbox, FormControlLabel, useMediaQuery, Tab, Tabs, Typography} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormBody } from "avrora";
import { ConfigurationContext, ConfigurationProvider } from "../../App";
import { Check } from "@mui/icons-material";

export const Config = () => {
    const [tab, setTab] = useState('interface');

    return (
        <ConfigurationProvider>
            <Stack spacing={2}>
                <Tabs
                    value={tab}
                    onChange={(_e, value) => {
                        setTab(value);
                    }}
                >
                    <Tab value="interface" label="Настройки интерфейса" />
                    <Tab value="privacy" label="Политика обработки персональных данных" />
                </Tabs>

                {tab === "interface" ? (
                    <Page />
                ) : (
                    <PrivacyPolicyPage />
                )}
            </Stack>
        </ConfigurationProvider>
    );
};

export const Page = () => {
    const [configuration, setConfiguration] = useContext(ConfigurationContext);
    const excludedProperties = ['textOfPersonalInfoProcessingAgreement', 'linkToPersonalInfoProcessingPolicy', 'requirePersonalInfoProcessingConfirmation'];
    
    const getFilteredConfiguration = (config: any) => {
        return Object.keys(config)
            .filter(item => !excludedProperties.includes(item))
            .reduce<any>((coll, item) => [...coll, { propertyName: item, propertyValue: config[item] }], []);
    };

    const [value, setValue] = useState(getFilteredConfiguration(configuration));
    const isMobile = useMediaQuery("(max-width:499px)");

    useEffect(() => {
        setValue(getFilteredConfiguration(configuration));
    }, [configuration]);

    const getPropertyLabel = (propertyName: string) => {
        if (propertyName === 'defaultSupportEmail') {
            return 'Ящик поддержки';
        }
        if (propertyName === 'bottomPageInfo') {
            return 'Текст футера';
        }
        return propertyName;
    };

    const disabled = useMemo(
        () =>
            JSON.stringify(getFilteredConfiguration(configuration)) === JSON.stringify(value),
        [value, configuration]
    );

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "auto" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
        >
            <Typography variant="caption" color="text.secondary">
                Не указывайте секретные данные, данные доступны без аутентификации
            </Typography>
            {value?.map((i: any, j: number) => {
                const isBoolean = typeof i?.propertyValue === 'boolean';
                const propertyLabel = getPropertyLabel(i?.propertyName);
                
                return (
                    <Stack key={j} spacing={1}>
                        {isBoolean ? (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={i?.propertyValue === true}
                                        onChange={(e) =>
                                            setValue((old: FormBody) =>
                                                old.map((ii: any, jj: number) => (jj === j ? { ...ii, propertyValue: e.target.checked } : ii))
                                            )
                                        }
                                    />
                                }
                                label={propertyLabel}
                            />
                        ) : (
                            <TextField
                                size="small"
                                label={propertyLabel}
                                fullWidth
                                multiline={i?.propertyName === 'bottomPageInfo'}
                                rows={i?.propertyName === 'bottomPageInfo' ? 4 : undefined}
                                slotProps={{
                                    input: {
                                        autoComplete: "new-password",
                                    },
                                }}
                                value={i?.propertyValue}
                                onChange={(e) =>
                                    setValue((old: FormBody) =>
                                        old.map((ii: any, jj: number) => (jj === j ? { ...ii, propertyValue: e.target.value } : ii))
                                    )
                                }
                            />
                        )}
                        {i?.propertyName === 'bottomPageInfo' && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                будет показан на всех пользовательских страницах
                            </Typography>
                        )}
                    </Stack>
                );
            })}

            <Stack direction="row" sx={{ gap: isMobile ? 1 : 2 }} flexWrap="wrap" gap={1}>
                <Button
                    disabled={disabled}
                    onClick={() =>
                        setConfiguration(
                            value.reduce((coll: any, item: any) => ({ ...coll, [item.propertyName]: item.propertyValue }), {})
                        )
                    }
                    startIcon={<Check />}
                    size="medium"
                    fullWidth={isMobile}
                >
                    Сохранить
                </Button>
            </Stack>
        </Stack>
    );
};

export const PrivacyPolicyPage = () => {
    const [configuration, setConfiguration] = useContext(ConfigurationContext);
    const isMobile = useMediaQuery("(max-width:499px)");

    const [requirePersonalInfoProcessingConfirmation, setRequirePersonalInfoProcessingConfirmation] = useState(
        configuration.requirePersonalInfoProcessingConfirmation ?? false
    );
    const [linkToPersonalInfoProcessingPolicy, setLinkToPersonalInfoProcessingPolicy] = useState(
        configuration.linkToPersonalInfoProcessingPolicy ?? ''
    );
    const [textOfPersonalInfoProcessingAgreement, setTextOfPersonalInfoProcessingAgreement] = useState(
        configuration.textOfPersonalInfoProcessingAgreement ?? ''
    );

    useEffect(() => {
        setRequirePersonalInfoProcessingConfirmation(configuration.requirePersonalInfoProcessingConfirmation ?? false);
        setLinkToPersonalInfoProcessingPolicy(configuration.linkToPersonalInfoProcessingPolicy ?? '');
        setTextOfPersonalInfoProcessingAgreement(configuration.textOfPersonalInfoProcessingAgreement ?? '');
    }, [configuration]);

    const disabled = useMemo(
        () =>
            configuration.requirePersonalInfoProcessingConfirmation === requirePersonalInfoProcessingConfirmation &&
            configuration.linkToPersonalInfoProcessingPolicy === linkToPersonalInfoProcessingPolicy &&
            configuration.textOfPersonalInfoProcessingAgreement === textOfPersonalInfoProcessingAgreement,
        [requirePersonalInfoProcessingConfirmation, linkToPersonalInfoProcessingPolicy, textOfPersonalInfoProcessingAgreement, configuration]
    );

    const handleSave = () => {
        setConfiguration({
            ...configuration,
            requirePersonalInfoProcessingConfirmation,
            linkToPersonalInfoProcessingPolicy,
            textOfPersonalInfoProcessingAgreement,
        });
    };

    return (
        <Stack
            maxWidth={isMobile ? "100%" : "1024px"}
            minWidth={isMobile ? "auto" : "1024px"}
            padding={isMobile ? "1rem" : "3rem"}
            spacing={2}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={requirePersonalInfoProcessingConfirmation}
                        onChange={(e) => setRequirePersonalInfoProcessingConfirmation(e.target.checked)}
                    />
                }
                label="Требовать подтверждение обработки персональных данных"
            />

            <TextField
                size="small"
                label="Ссылка на политику обработки персональных данных"
                fullWidth
                value={linkToPersonalInfoProcessingPolicy}
                onChange={(e) => setLinkToPersonalInfoProcessingPolicy(e.target.value)}
                slotProps={{
                    input: {
                        autoComplete: "new-password",
                    },
                }}
            />

            <TextField
                size="small"
                label="Текст соглашения об обработке персональных данных"
                fullWidth
                multiline
                rows={4}
                value={textOfPersonalInfoProcessingAgreement}
                onChange={(e) => setTextOfPersonalInfoProcessingAgreement(e.target.value)}
                slotProps={{
                    input: {
                        autoComplete: "new-password",
                    },
                }}
            />

            <Stack direction="row" sx={{ gap: isMobile ? 1 : 2 }} flexWrap="wrap" gap={1}>
                <Button
                    disabled={disabled}
                    onClick={handleSave}
                    startIcon={<Check />}
                    size="medium"
                    fullWidth={isMobile}
                >
                    Сохранить
                </Button>
            </Stack>
        </Stack>
    );
};
